import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productsAPI } from '../api/products';
import { categoriesAPI } from '../api/categories';
import ProductCard from '../components/common/ProductCard';
import Loader from '../components/common/Loader';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || '',
    sort: 'newest',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const data = await categoriesAPI.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.category) params.category_id = filters.category;
      if (filters.search) params.search = filters.search;
      
      const data = await productsAPI.getAll(params);
      
      let sorted = [...data];
      if (filters.sort === 'price_low') {
        sorted.sort((a, b) => (a.discount_price || a.original_price) - (b.discount_price || b.original_price));
      } else if (filters.sort === 'price_high') {
        sorted.sort((a, b) => (b.discount_price || b.original_price) - (a.discount_price || a.original_price));
      } else if (filters.sort === 'newest') {
        sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      }
      
      setProducts(sorted);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setFilters({ ...filters, category: categoryId });
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      search: '',
      sort: 'newest',
    });
  };

  const getSelectedCategoryName = () => {
    if (!filters.category) return null;
    const category = categories.find(c => c.id.toString() === filters.category);
    return category?.name;
  };

  const hasActiveFilters = filters.category !== '';

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-xl lg:text-2xl font-light tracking-tight text-gray-900">
          All Products
          <span className="text-sm text-gray-400 font-normal ml-2">
            ({products.length})
          </span>
        </h1>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="lg:hidden text-gray-400 hover:text-gray-900 transition"
        >
          <FunnelIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Active Filter Indicator */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 mb-6">
          <span className="text-xs text-gray-400">Active filter:</span>
          <span className="text-xs text-gray-900 capitalize">{getSelectedCategoryName()}</span>
          <button
            onClick={clearFilters}
            className="text-gray-400 hover:text-gray-900 transition"
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-10">
        {/* Filters Sidebar */}
        <div className={`${showFilters ? 'fixed inset-0 z-50 bg-white p-6 overflow-auto' : 'hidden'} lg:block lg:relative lg:bg-transparent lg:p-0 lg:w-64 flex-shrink-0`}>
          {showFilters && (
            <div className="flex justify-between items-center mb-6 lg:hidden">
              <h2 className="text-sm font-light tracking-wide uppercase text-gray-900">Filters</h2>
              <button onClick={() => setShowFilters(false)} className="text-gray-400">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          )}
          
          <div className="space-y-8">
            {/* Sort By */}
            <div>
              <h3 className="text-xs font-light tracking-wide uppercase text-gray-400 mb-3">
                Sort By
              </h3>
              <select
                value={filters.sort}
                onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                className="w-full px-0 py-2 text-sm text-gray-900 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition bg-transparent"
              >
                <option value="newest">Newest First</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
              </select>
            </div>

            {/* Categories */}
            <div>
              <h3 className="text-xs font-light tracking-wide uppercase text-gray-400 mb-3">
                Category
              </h3>
              {categoriesLoading ? (
                <div className="py-4">
                  <div className="w-4 h-4 border border-gray-300 border-t-gray-900 rounded-full animate-spin"></div>
                </div>
              ) : (
                <div className="space-y-2">
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`block text-sm transition ${filters.category === '' ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    All Categories
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id.toString())}
                      className={`block text-sm transition ${filters.category === category.id.toString() ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      {category.name}
                      {category.product_count > 0 && (
                        <span className="ml-1 text-xs text-gray-400">({category.product_count})</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-400">No products found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-8">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Filter Overlay */}
      {showFilters && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setShowFilters(false)}
        />
      )}
    </div>
  );
};

export default Products;